import React from "react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import AdminApiTable from "@/modules/racket_lab_admin/AdminApiTable";
import ConfirmActionDialog from "@/modules/racket_lab_admin/ConfirmActionDialog";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminCapabilities — Sub-sprint B.
 * Two tabs (read-only catalogue + shop-capabilities w/ toggle).
 * No hard-delete from UI; status toggle preferred.
 *
 * QUICK 1.D i18n migration: headers, two section titles, both tables'
 * columns, status badges, toggle dialog and empty states resolved via
 * `racketLab.capabilities.*`. Capability `key` payload value stays
 * untouched (technical identifier).
 */
const RLAdminCapabilities = () => {
  const t = useRacketLabAdminT();
  const catalogue = useAdminApi("/admin/racket-lab/capabilities");
  const shopCaps = useAdminApi("/admin/racket-lab/shop-capabilities");
  const { run, loading: mutating } = useAdminMutation();

  const catRows = Array.isArray(catalogue.data) ? catalogue.data : catalogue.data?.items || [];
  const shopRows = Array.isArray(shopCaps.data) ? shopCaps.data : shopCaps.data?.items || [];

  const toggleShopCap = (sc) => async () => {
    const next = sc.status === "active" ? "inactive" : "active";
    await run("post", `/admin/racket-lab/shop-capabilities/${sc.id}/status`, { status: next }).catch(() => {});
    shopCaps.reload();
  };

  return (
    <div data-testid="rl-admin-capabilities-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />
      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("capabilities.page_header")}</h1>
        <p className="text-xs text-slate-500">{t("capabilities.subtitle")}</p>
      </header>

      <section data-testid="rl-cap-catalogue">
        <h2 className="mb-2 text-sm font-medium text-slate-700">{t("capabilities.catalogue_section_title")}</h2>
        <AdminApiTable
          columns={[
            { key: "key", label: t("capabilities.columns.key"), render: (r) => <code className="text-xs">{r.key}</code> },
            { key: "display_name", label: t("capabilities.columns.display_name") },
            { key: "status", label: t("capabilities.columns.status") },
          ]}
          rows={catRows} loading={catalogue.loading} error={catalogue.error}
          emptyLabel={t("capabilities.empty_catalogue")} dataTestId="rl-cap-catalogue-table"
        />
      </section>

      <section data-testid="rl-shopcap-section">
        <h2 className="mb-2 text-sm font-medium text-slate-700">{t("capabilities.shopcap_section_title")}</h2>
        <AdminApiTable
          columns={[
            { key: "racket_lab_shop_id", label: t("capabilities.columns.shop"), render: (r) => (
              <code className="text-[10px] text-slate-500">{r.racket_lab_shop_id?.slice(0, 18) || "—"}</code>
            )},
            { key: "capability_key", label: t("capabilities.columns.capability") },
            { key: "status", label: t("capabilities.columns.status"), render: (r) => (
              <span data-testid={`rl-shopcap-status-${r.id}`} className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                r.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"
              }`}>{r.status || "—"}</span>
            )},
            { key: "actions", label: t("capabilities.columns.actions"), render: (r) => (
              <ConfirmActionDialog title={t("capabilities.toggle_title")} confirmLabel={t("capabilities.toggle_confirm_label")}
                onConfirm={toggleShopCap(r)} disabled={mutating} dataTestId={`rl-shopcap-toggle-${r.id}`}
                trigger={<button type="button" className="rounded bg-slate-100 px-2 py-1 text-[10px] hover:bg-slate-200">{t("capabilities.toggle_btn")}</button>}
              />
            )},
          ]}
          rows={shopRows} loading={shopCaps.loading} error={shopCaps.error}
          emptyLabel={t("capabilities.empty_shopcap")} dataTestId="rl-shopcap-table"
        />
      </section>
    </div>
  );
};

export default RLAdminCapabilities;
